/**
 * ValuationWidget - Display valuation gauge with intrinsic value vs current price
 *
 * Shows:
 * - Intrinsic value and upside/downside percentage
 * - Horizontal bar with zones: green (attractive), yellow (fair ±20%), red (high price)
 * - Current price marker with volume-profile-style fill
 * - Valuation price marker with volume-profile-style fill
 * - Safety margin indicator
 * - Analysis notes
 */

import { useState, useEffect, useMemo } from 'react';
import { detectDarkMode, observeDarkMode, WIDGET_BACKGROUND } from './chartTheme';

// Zone colors
const ZONE_COLORS = {
    attractive: '#22c55e',      // Green - Vùng giá hấp dẫn
    attractiveFill: '#166534',  // Dark green for volume-profile fill
    fair: '#eab308',            // Yellow - Mức định giá ± 20%
    high: '#ef4444',            // Red - Vùng giá cao
};

// Format price in VND
function formatPrice(value) {
    if (value === null || value === undefined) return '-';
    return Math.round(value).toLocaleString('vi-VN');
}

// Format percentage
function formatPercent(value, decimals = 2) {
    if (value === null || value === undefined) return '-';
    return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}%`;
}

/**
 * Calculate valuation zones and positions using API data structure
 */
function calculateValuationData(intrinsicValueData) {
    if (!intrinsicValueData) return null;

    const {
        intrinsicValue,
        priceClose,
        fairValueDelta,
        fairValueLow,
        fairValueMedian,
        fairValueHigh,
    } = intrinsicValueData;

    if (!intrinsicValue || !priceClose) return null;

    // Calculate range - max should be the higher of fairValueHigh.y or current values
    const maxRange = Math.max(
        fairValueHigh?.y || 0,
        priceClose * 1.2,
        intrinsicValue * 1.2
    );
    const minRange = 0;
    const range = maxRange - minRange;

    // Zone boundaries from API data
    const zone1End = fairValueLow?.y || 0;          // End of attractive zone
    const zone2End = fairValueMedian?.y || 0;       // End of fair zone
    const zone3End = fairValueHigh?.y || maxRange;  // End of high zone

    // Calculate positions as percentages
    const attractiveEndPct = ((zone1End - minRange) / range) * 100;
    const fairEndPct = ((zone2End - minRange) / range) * 100;
    const currentPricePct = ((priceClose - minRange) / range) * 100;
    const intrinsicPct = ((intrinsicValue - minRange) / range) * 100;

    return {
        safetyMargin: fairValueDelta,
        attractiveEndPct,
        fairEndPct,
        currentPricePct: Math.min(100, Math.max(0, currentPricePct)),
        intrinsicPct: Math.min(100, Math.max(0, intrinsicPct)),
        currentPrice: priceClose,
        intrinsicValue,
        zone1End,
        zone2End,
        zone3End,
    };
}

/**
 * Valuation Gauge Bar Component
 */
function ValuationGauge({ data, ticker, isDarkMode }) {
    const valuationData = data;

    if (!valuationData) return null;

    const {
        attractiveEndPct,
        fairEndPct,
        currentPricePct,
        intrinsicPct,
        safetyMargin,
        currentPrice,
        intrinsicValue,
    } = valuationData;

    return (
        <div className="relative pb-4">
            {/* Safety margin label with line connector */}
            <div className="absolute -top-10 text-center"
                 style={{
                     left: `${(currentPricePct + intrinsicPct) / 2}%`,
                     transform: 'translateX(-50%)',
                 }}>
                <div className={`text-sm font-semibold ${safetyMargin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {formatPercent(safetyMargin, 2)}
                </div>
                <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Biên an toàn</div>
                {/* Vertical line connector - only to middle of bar */}
                <div className="absolute left-1/2 w-px"
                     style={{
                         top: '100%',
                         height: '52px',
                         transform: 'translateX(-50%)',
                         backgroundColor: '#ffffff',
                         zIndex: 10,
                     }}></div>
            </div>

            {/* Container for bars and vertical lines */}
            <div className="relative mt-12">
                {/* Vertical lines - different heights for each fill */}
                <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }}>
                    {/* Current price vertical line - extends above */}
                    <div
                        className="absolute"
                        style={{
                            left: `${currentPricePct}%`,
                            transform: 'translateX(-50%)',
                            height: '32px',
                            top: '-8px',
                            width: '2px',
                            backgroundColor: 'rgba(156, 163, 175, 0.3)',
                        }}
                    />
                    {/* Intrinsic value vertical line - extends above */}
                    <div
                        className="absolute"
                        style={{
                            left: `${intrinsicPct}%`,
                            transform: 'translateX(-50%)',
                            height: '96px',
                            top: '-8px',
                            width: '2px',
                            backgroundColor: 'rgba(156, 163, 175, 0.3)',
                        }}
                    />
                    {/* Horizontal line connecting the two vertical lines */}
                    <div
                        className="absolute"
                        style={{
                            left: `${Math.min(currentPricePct, intrinsicPct)}%`,
                            width: `${Math.abs(currentPricePct - intrinsicPct)}%`,
                            height: '2px',
                            top: '-8px',
                            backgroundColor: safetyMargin >= 0 ? 'rgba(22, 163, 74, 0.5)' : 'rgba(239, 68, 68, 0.5)',
                        }}
                    />
                </div>

                {/* Single bar with layered fills */}
                <div className="relative h-28 rounded-lg overflow-visible">
                    {/* Layer 1: Background zones (full height, solid colors) */}
                    <div className="absolute inset-0 flex rounded-lg overflow-hidden">
                        {/* Green zone */}
                        <div style={{ width: `${attractiveEndPct}%`, backgroundColor: ZONE_COLORS.attractive }} />
                        {/* Yellow zone */}
                        <div style={{ width: `${fairEndPct - attractiveEndPct}%`, backgroundColor: ZONE_COLORS.fair }} />
                        {/* Red zone */}
                        <div style={{ width: `${100 - fairEndPct}%`, backgroundColor: ZONE_COLORS.high }} />
                    </div>

                    {/* Layer 2: Current price fill (top, with margin) */}
                    <div
                        className="absolute left-0 rounded-r"
                        style={{
                            width: `${currentPricePct}%`,
                            top: '8px',
                            height: 'calc(35% - 8px)',
                        }}
                    >
                        {/* Fill background */}
                        <div className="absolute inset-0 rounded-r" style={{ backgroundColor: 'rgba(22, 101, 52, 0.6)' }} />
                        {/* Text layer */}
                        <div className="relative h-full flex items-center justify-end pr-3">
                            <div className="text-right">
                                <div className="text-[9px] text-white whitespace-nowrap leading-tight">Giá cổ phiếu hiện tại</div>
                                <div className="text-xs font-bold text-white">{formatPrice(currentPrice)}</div>
                            </div>
                        </div>
                    </div>

                    {/* Layer 3: Intrinsic value fill (bottom, with margin) */}
                    <div
                        className="absolute left-0 rounded-r"
                        style={{
                            width: `${intrinsicPct}%`,
                            bottom: '8px',
                            height: 'calc(35% - 8px)',
                        }}
                    >
                        {/* Fill background */}
                        <div className="absolute inset-0 rounded-r" style={{ backgroundColor: 'rgba(22, 101, 52, 0.6)' }} />
                        {/* Text layer */}
                        <div className="relative h-full flex items-center justify-end pr-3">
                            <div className="text-right">
                                <div className="text-[9px] text-white whitespace-nowrap leading-tight">Mức định giá</div>
                                <div className="text-xs font-bold text-white">{formatPrice(intrinsicValue)}</div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}

/**
 * Ticker Tabs Component
 */
function TickerTabs({ tickers, activeTicker, onTickerChange }) {
    return (
        <div className="flex border-b border-gray-200 dark:border-gray-700">
            {tickers.map(ticker => (
                <button
                    key={ticker}
                    onClick={() => onTickerChange(ticker)}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                        activeTicker === ticker
                            ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                >
                    {ticker}
                </button>
            ))}
        </div>
    );
}

/**
 * Ticker Content Component (without outer border)
 */
function TickerContent({ tickerData, ticker, isDarkMode, showBorder = true }) {
    const [isMethodsExpanded, setIsMethodsExpanded] = useState(false);

    // Extract valuation data
    const valuationInfo = useMemo(() => {
        if (!tickerData?.intrinsicValue) return null;

        const valuationData = calculateValuationData(tickerData.intrinsicValue);
        if (!valuationData) return null;

        return {
            ...valuationData,
            ticker: tickerData.ticker || ticker,
            name: tickerData.name || '',
        };
    }, [tickerData, ticker]);

    // No data
    if (!valuationInfo) {
        return null;
    }

    const { intrinsicValue, currentPrice, safetyMargin } = valuationInfo;
    const upside = ((intrinsicValue - currentPrice) / currentPrice) * 100;
    const isPositive = upside >= 0;

    const content = (
        <>
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between">
                    <div>
                        {/* Upside indicator */}
                        <div className={`text-sm font-medium ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {isPositive ? '↑' : '↓'} ({formatPercent(Math.abs(upside), 2).replace('+', '')} {isPositive ? 'Upside' : 'Downside'})
                        </div>
                        {/* Intrinsic value */}
                        <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                {formatPrice(intrinsicValue)}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">vnđ/cổ phiếu</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Giá trị nội tại</div>
                    </div>
                    <div className="text-right">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{ticker}</div>
                        {valuationInfo.name && <div className="text-xs text-gray-500 dark:text-gray-400">{valuationInfo.name}</div>}
                    </div>
                </div>
            </div>

            {/* Valuation Gauge */}
            <div className="px-4 py-1">
                <ValuationGauge
                    data={valuationInfo}
                    ticker={ticker}
                    isDarkMode={isDarkMode}
                />
            </div>

            {/* Legend */}
            <div className="px-4 pb-4 flex flex-wrap items-center justify-center gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: ZONE_COLORS.attractive }}></span>
                    <span className="text-gray-600 dark:text-gray-400">Vùng giá hấp dẫn</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: ZONE_COLORS.fair }}></span>
                    <span className="text-gray-600 dark:text-gray-400">Mức định giá ± 20%</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: ZONE_COLORS.high }}></span>
                    <span className="text-gray-600 dark:text-gray-400">Vùng giá cao</span>
                </div>
            </div>

            {/* Valuation Methods Table - Collapsible */}
            {tickerData?.combined && (
                <div className="px-4 pb-4">
                    <button
                        onClick={() => setIsMethodsExpanded(!isMethodsExpanded)}
                        className="flex items-center gap-2 w-full text-left py-2"
                    >
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Các phương pháp định giá</h3>
                        <svg
                            className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${isMethodsExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {isMethodsExpanded && (
                        <div className="overflow-x-auto mt-3">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <th className="text-left py-2 px-2 text-gray-600 dark:text-gray-400 font-medium">Chỉ số</th>
                                        <th className="text-right py-2 px-2 text-gray-600 dark:text-gray-400 font-medium">Giá trị 1 cổ phiếu (VNĐ)</th>
                                        <th className="text-right py-2 px-2 text-gray-600 dark:text-gray-400 font-medium">Tỷ trọng (%)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tickerData.combined.weightPe > 0 && (
                                        <tr className="border-b border-gray-100 dark:border-gray-800">
                                            <td className="py-2 px-2 text-gray-700 dark:text-gray-300">P/E</td>
                                            <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100">{formatPrice(tickerData.combined.fairValueByPe)}</td>
                                            <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100">{tickerData.combined.weightPe}%</td>
                                        </tr>
                                    )}
                                    {tickerData.combined.weightEvEbitda > 0 && (
                                        <tr className="border-b border-gray-100 dark:border-gray-800">
                                            <td className="py-2 px-2 text-gray-700 dark:text-gray-300">EV/EBITDA</td>
                                            <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100">{formatPrice(tickerData.combined.fairValueByEvEbitda)}</td>
                                            <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100">{tickerData.combined.weightEvEbitda}%</td>
                                        </tr>
                                    )}
                                    {tickerData.combined.weightPb > 0 && (
                                        <tr className="border-b border-gray-100 dark:border-gray-800">
                                            <td className="py-2 px-2 text-gray-700 dark:text-gray-300">P/B</td>
                                            <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100">{formatPrice(tickerData.combined.fairValueByPb)}</td>
                                            <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100">{tickerData.combined.weightPb}%</td>
                                        </tr>
                                    )}
                                    {tickerData.combined.weightEvSales > 0 && (
                                        <tr className="border-b border-gray-100 dark:border-gray-800">
                                            <td className="py-2 px-2 text-gray-700 dark:text-gray-300">EV/Sales</td>
                                            <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100">{formatPrice(tickerData.combined.fairValueByEvSales)}</td>
                                            <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100">{tickerData.combined.weightEvSales}%</td>
                                        </tr>
                                    )}
                                    {tickerData.combined.weightEpv > 0 && (
                                        <tr className="border-b border-gray-100 dark:border-gray-800">
                                            <td className="py-2 px-2 text-gray-700 dark:text-gray-300">Chiết khấu dòng tiền</td>
                                            <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100">{formatPrice(tickerData.combined.fairValueByEpv)}</td>
                                            <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100">{tickerData.combined.weightEpv}%</td>
                                        </tr>
                                    )}
                                    {tickerData.combined.weightExcessReturn > 0 && (
                                        <tr className="border-b border-gray-100 dark:border-gray-800">
                                            <td className="py-2 px-2 text-gray-700 dark:text-gray-300">Excess Return</td>
                                            <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100">{formatPrice(tickerData.combined.fairValueByExcessReturn)}</td>
                                            <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100">{tickerData.combined.weightExcessReturn}%</td>
                                        </tr>
                                    )}
                                    {/* Final result row */}
                                    <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50">
                                        <td className="py-2 px-2 font-semibold text-gray-900 dark:text-gray-100">Kết quả định giá cuối cùng (VNĐ/CP)</td>
                                        <td className="py-2 px-2 text-right font-bold text-gray-900 dark:text-gray-100">{formatPrice(tickerData.combined.overallIntrinsicValue)}</td>
                                        <td className="py-2 px-2 text-right font-semibold text-gray-900 dark:text-gray-100">100%</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Disclaimer */}
            <div className="px-4 pb-4 pt-0">
                <div className="flex items-start gap-2">
                    <span className="text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5">⚠</span>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-semibold">Miễn trừ trách nhiệm: </span>
                        Số liệu về Giá trị nội tại chỉ dành cho mục đích tham khảo và không phải là yếu tố duy nhất quyết định đến giá cổ phiếu. Simplize miễn trừ mọi trách nhiệm có liên quan. Vui lòng xem chi tiết ở{' '}
                        <a
                            href="https://simplize.vn/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 hover:underline"
                        >
                            Điều khoản sử dụng
                        </a>
                    </p>
                </div>
            </div>

            {/* View on Simplize Button */}
            <div className="px-4 py-3 flex justify-center border-t border-gray-100 dark:border-gray-700">
                <a
                    href={`https://simplize.vn/co-phieu/${ticker}/phan-tich`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors"
                >
                    Xem phân tích 360 trên Simplize
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </a>
            </div>
        </>
    );

    if (showBorder) {
        return (
            <div className="border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden"
                 style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
                {content}
            </div>
        );
    }

    return content;
}

/**
 * Main Valuation Widget - supports multiple tickers
 */
export default function ValuationWidget({ data: initialData, config }) {
    const [isDarkMode, setIsDarkMode] = useState(detectDarkMode);

    useEffect(() => {
        return observeDarkMode(setIsDarkMode);
    }, []);

    // Extract config
    const { params = {} } = config || {};
    const rawTickers = params.tickers || [];
    const tickers = Array.isArray(rawTickers) ? rawTickers : (typeof rawTickers === 'string' ? [rawTickers] : []);

    // Parse data to extract all tickers
    const tickersData = useMemo(() => {
        if (!initialData) return {};

        // Check if data is nested in "data" field
        const actualData = initialData.data || initialData;

        // If single ticker response with intrinsicValue at top level
        if (actualData.intrinsicValue) {
            const ticker = tickers[0] || actualData.ticker || 'UNKNOWN';
            return { [ticker]: actualData };
        }

        // Multi-ticker response - data is an object with ticker keys
        if (typeof actualData === 'object') {
            const result = {};
            for (const key in actualData) {
                // Skip metadata fields
                if (key === 'total' || key === 'error' || key === 'status' || key === 'message') {
                    continue;
                }
                // Check if this is a ticker data object
                if (actualData[key] && typeof actualData[key] === 'object' && actualData[key].intrinsicValue) {
                    result[key] = actualData[key];
                }
            }
            return result;
        }

        return {};
    }, [initialData, tickers]);

    const tickerKeys = Object.keys(tickersData);
    const [activeTicker, setActiveTicker] = useState(tickerKeys[0] || '');

    // Update active ticker when data changes
    useEffect(() => {
        if (tickerKeys.length > 0 && !tickerKeys.includes(activeTicker)) {
            setActiveTicker(tickerKeys[0]);
        }
    }, [tickerKeys, activeTicker]);

    // No data
    if (tickerKeys.length === 0) {
        return (
            <div className="border border-gray-100 dark:border-gray-800 rounded-xl p-4 text-center text-gray-500"
                 style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
                Không có dữ liệu định giá
            </div>
        );
    }

    // Render with single container, show tabs only if multiple tickers
    const currentTicker = tickerKeys.length === 1 ? tickerKeys[0] : activeTicker;

    return (
        <div className="border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden"
             style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
            {/* Show tabs only when there are multiple tickers */}
            {tickerKeys.length > 1 && (
                <TickerTabs
                    tickers={tickerKeys}
                    activeTicker={activeTicker}
                    onTickerChange={setActiveTicker}
                />
            )}
            <TickerContent
                tickerData={tickersData[currentTicker]}
                ticker={currentTicker}
                isDarkMode={isDarkMode}
                showBorder={false}
            />
        </div>
    );
}
